import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadGameCategory } from "../../../../features/games/services/casino.service";
import { GameCategoryDataList } from "../../../../features/games/models/responses/GameCategoryListResponse";
import { BLOB_IMAGE_URL } from "../../../../config/appConfig";
import { BlobContainerConsts } from "../../../../models/constants/blobContainerConsts";
import { useLanguage } from "../../../../config/localization/LanguageProvider";
import { SupportedLanguages } from "../../../../models/constants/supportedLanguages";
import type {
  ActiveVerticalDetail,
  ActiveVerticalResponse,
} from "../../../../features/home/models";
import homeService from "../../../../features/home/services/home.service";
import localization from "../../../../config/localization";
import { GameVertical } from "../../../../models/constants/gameVertical";

const SPORTS_VERTICAL = GameVertical.Sports.toLowerCase();
const SAP_VERTICAL = GameVertical.SAP.toLowerCase();
const CASINO_VERTICAL = GameVertical.Casino.toLowerCase();

const filterActiveVerticalsForNav = (
  details: ActiveVerticalDetail[],
  userVerticalsCsv: string | null | undefined,
) => {
  const userVerticalsList = (userVerticalsCsv ?? "")
    .toLowerCase()
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  return details.filter((vertical) => {
    const nameLower = vertical.name.toLowerCase();
    const isSportsIndia =
      nameLower === GameVertical.SportsIndia.toLowerCase() ||
      nameLower === "sports india";

    if (nameLower === SPORTS_VERTICAL) {
      return !userVerticalsList.includes(SPORTS_VERTICAL);
    }
    if (isSportsIndia) {
      return !userVerticalsList.includes(SAP_VERTICAL);
    }
    return !userVerticalsList.some((x) => x === nameLower);
  });
};

export function GamesMenu({
  onClose,
  isAuthenticated,
}: {
  onClose: () => void;
  isAuthenticated: boolean;
}) {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const [categories, setCategories] = useState<GameCategoryDataList[]>([]);
  const [verticals, setVerticals] = useState<ActiveVerticalResponse>();
  const [userVerticalsCsv, setUserVerticalsCsv] = useState<string | null>(null);

  useEffect(() => {
    const casinoEnabledInProduct =
      verticals?.details?.some(
        (v) => v.name.toLowerCase() === CASINO_VERTICAL,
      ) ?? false;

    let userClosedCasino = false;
    if (isAuthenticated && userVerticalsCsv) {
      userClosedCasino = userVerticalsCsv
        .toLowerCase()
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .includes(CASINO_VERTICAL);
    }

    const shouldLoadCategories =
      verticals !== undefined && casinoEnabledInProduct && !userClosedCasino;

    if (!shouldLoadCategories) {
      setCategories([]);
      return;
    }

    const fetchCategories = async () => {
      try {
        const categoryResponse = await loadGameCategory();

        const sortedCategories =
          categoryResponse.gameCategoryListResponse?.gameCategoryList?.sort(
            (a, b) => a.name.localeCompare(b.name),
          ) || [];

        setCategories(sortedCategories);
      } catch (error) {
        console.error("Failed to load categories:", error);
      }
    };

    fetchCategories();
  }, [verticals, isAuthenticated, userVerticalsCsv]);

  useEffect(() => {
    const fetchActiveVerticals = async () => {
      try {
        const response = await homeService.fetchActiveVerticals();

        if (response.isSuccess) {
          setVerticals(response.data ?? undefined);
        }

        if (isAuthenticated) {
          const userRes = await homeService.fetchUserVerticals();
          if (userRes.isSuccess && userRes.data?.verticalsCsv != null) {
            setUserVerticalsCsv(userRes.data.verticalsCsv);
          } else {
            setUserVerticalsCsv(null);
          }
        } else {
          setUserVerticalsCsv(null);
        }
      } catch (error) {
        console.error("Failed to load verticals:", error);
      }
    };

    fetchActiveVerticals();
  }, [isAuthenticated]);

  const filteredVerticals = useMemo(() => {
    const nonCasino =
      verticals?.details?.filter(
        (vertical) => vertical.name.toLowerCase() !== "casino",
      ) ?? [];

    if (!isAuthenticated || !userVerticalsCsv) {
      return nonCasino;
    }
    return filterActiveVerticalsForNav(nonCasino, userVerticalsCsv);
  }, [verticals, isAuthenticated, userVerticalsCsv]);

  const showSportsIndia = useMemo(() => {
    if (!isAuthenticated || !userVerticalsCsv) {
      return true;
    }
    const list = userVerticalsCsv
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return !list.includes(SAP_VERTICAL);
  }, [isAuthenticated, userVerticalsCsv]);

  /** Same rules as Header: admin-off removes Casino from active verticals; CSV can close it per user. */
  const showCasinoSection = useMemo(() => {
    const casinoEnabledInProduct =
      verticals?.details?.some(
        (v) => v.name.toLowerCase() === CASINO_VERTICAL,
      ) ?? false;

    if (!casinoEnabledInProduct) {
      return false;
    }
    if (!isAuthenticated || !userVerticalsCsv) {
      return true;
    }
    const list = userVerticalsCsv
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    return !list.includes(CASINO_VERTICAL);
  }, [verticals, isAuthenticated, userVerticalsCsv]);

  const verticalGridClass = useMemo(() => {
    const totalItems = filteredVerticals.length + (showSportsIndia ? 1 : 0);

    if (totalItems <= 0) return "grid-cols-2";
    if (totalItems <= 2) return "grid-cols-2";
    if (totalItems === 3) return "grid-cols-3";
    return "grid-cols-4";
  }, [filteredVerticals, showSportsIndia]);

  const onCategoryClick = (categoryId: string) => {
    navigate("/casino", {
      state: {
        selectedCategoryId: categoryId,
      },
    });

    onClose();
  };

  const fetchIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case "exchange":
        return "/icons/ic-exchange.svg";
      case "casino":
        return "/icons/ic-casino.svg";
      case "sports":
        return "/icons/ic-sports.svg";
      default:
        return "/icons/ic-casino.svg";
    }
  };

  const fetchPath = (name: string) => {
    switch (name.toLowerCase()) {
      case "sports":
        return "/sportsbookplus";
      case "sports-india":
        return "/bharatsportsbook";
      case "exchange":
        return isAuthenticated ? "exchange/Wickets9" : "exchange";
      default:
        return `/${name.toLowerCase()}`;
    }
  };

  const hasTopVerticals = filteredVerticals.length > 0 || showSportsIndia;

  return (
    <div>
      {hasTopVerticals && (
        <div className="mx-4 mt-5 mb-4 border border-[#2E2E2E] bg-[#0F0F0F] rounded-xl p-4">
          <div className={`grid ${verticalGridClass} gap-5`}>
            {filteredVerticals.map((vertical) => (
              <div
                key={vertical.id}
                className="flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[#1f1f1f] rounded-xl p-2"
                onClick={() => {
                  navigate(fetchPath(vertical.name));
                  onClose();
                }}
              >
                <img
                  src={fetchIcon(vertical.name)}
                  alt={vertical.name}
                  className="h-12 w-12 object-contain mb-2"
                />
                <span className="text-white text-sm tracking-wider">
                  {localization.getString(vertical.name.toLowerCase())}
                </span>
              </div>
            ))}

            {showSportsIndia && (
              <div
                key="sports-india"
                className="flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[#1f1f1f] rounded-xl p-2"
                onClick={() => {
                  navigate(fetchPath("sports-india"));
                  onClose();
                }}
              >
                <img
                  src="/icons/ic-sports-india.svg"
                  alt="sports-india"
                  className="h-12 w-12 object-contain mb-2"
                />
                <span className="text-white text-sm tracking-wider">
                  {localization.sportsIndia}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {showCasinoSection && (
        <div className="mx-4 mt-5 mb-4 border border-[#2E2E2E] bg-[#0F0F0F] rounded-xl p-4">
          <div className="grid grid-cols-4 gap-5">
            {categories.map((category) => (
              <div
                key={category.id}
                className="flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[#1f1f1f] rounded-xl p-2"
                onClick={() => onCategoryClick(category.id)}
              >
                <img
                  src={`${BLOB_IMAGE_URL}${BlobContainerConsts.GameCategory}/${category.categoryImagePath}`}
                  alt={category.name}
                  className="h-12 w-12 object-contain mb-2"
                />
                <span className="text-white text-sm tracking-wider">
                  {language === SupportedLanguages.English
                    ? category.name
                    : category.hindiName}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
